import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ShieldCheck, Euro } from 'lucide-react';
import type { IPolicyStatusCardProps } from '@/@types/insurance';
import type { TInvoiceStatus } from '@/@types/insurance';

export function PolicyStatusCard({
  isSubmitting,
  status,
  onStatusChange,
  invoiceStatus,
  onInvoiceStatusChange,
  premium,
  onPremiumChange,
  partiallyPaidAmount,
  onPartiallyPaidAmountChange,
}: IPolicyStatusCardProps) {
  return (
    <div className="rounded-xl border border-border bg-card p-6 flex flex-col gap-5">
      <div className="flex items-center gap-2.5">
        <ShieldCheck className="w-5 h-5 text-lime-700 dark:text-lime-400" />
        <h2 className="text-base font-semibold text-foreground">Policy Status</h2>
      </div>
      <div className="h-px bg-border" />

      <div className="grid grid-cols-2 gap-5">
        <div className="flex flex-col gap-1.5">
          <label className="text-[13px] font-medium text-foreground">Status</label>
          <Select
            value={status}
            onValueChange={val =>
              onStatusChange(val as 'draft' | 'active' | 'expired' | 'cancelled') // AS_JUSTIFIED: value comes from Select with known status options
            }
            disabled={isSubmitting}
          >
            <SelectTrigger className="w-full !h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[13px] font-medium text-foreground">Invoice</label>
          <Select
            value={invoiceStatus}
            onValueChange={val => onInvoiceStatusChange(val as TInvoiceStatus)} // AS_JUSTIFIED: value comes from Select with known invoice status options
            disabled={isSubmitting}
          >
            <SelectTrigger className="w-full !h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="not_paid">Not paid</SelectItem>
              <SelectItem value="partially_paid">Partially paid</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="unknown">Unknown</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[13px] font-medium text-foreground flex items-center gap-1.5">
            <Euro className="w-3.5 h-3.5" />
            Premium
          </label>
          <Input
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={premium ?? ''}
            onChange={e => {
              const val = e.target.value;
              onPremiumChange(val === '' ? undefined : parseFloat(val));
            }}
            disabled={isSubmitting}
            className="!h-10"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[13px] font-medium text-foreground flex items-center gap-1.5">
            <Euro className="w-3.5 h-3.5" />
            Partially Paid Amount
          </label>
          <Input
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={partiallyPaidAmount ?? ''}
            onChange={e => {
              const val = e.target.value;
              onPartiallyPaidAmountChange(val === '' ? undefined : parseFloat(val));
            }}
            disabled={invoiceStatus !== 'partially_paid' || isSubmitting}
            className="!h-10"
          />
        </div>
      </div>
    </div>
  );
}
